'use client';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import AureusLogo from '../components/AureusLogo';
import { challengeMFA, listMFAFactors, verifyAndActivateMFA } from '../lib/security/mfa';
import { useRecaptcha } from '../lib/recaptcha';
import { TENANT, appUrl } from '@/app/lib/tenant-config';

const TESTIMONIALS_ALL = [
  {name:"Thomas V.",role:"Gérant — agence web, Bruxelles",text:"J'avais peur de la paperasse ONSS. En 2 jours, Aureus m'a guidé de l'immatriculation jusqu'à la première fiche de paie. Zéro stress.",stars:5,lang:"fr"},
  {name:"Sarah M.",role:"Fondatrice — startup RH, Gand",text:"La Dimona en 8 secondes, c'est réel. J'ai engagé 3 employés en 1 semaine sans aucune connaissance en droit social belge.",stars:5,lang:"fr"},
  {name:"Karim B.",role:"Indépendant complémentaire, Liège",text:"Passage de mon ancien secrétariat social à Aureus en 3 jours. Reprise complète de mes données, comprise dans les frais de mise en route. Je sais ce que je paie chaque mois.",stars:5,lang:"fr"},
  {name:"Isabelle D.",role:"DRH — cabinet comptable, Namur",text:"Le module DmfA XML est parfait. Export direct vers l'ONSS, zéro rejet. Je gère 47 dossiers depuis un seul tableau de bord.",stars:5,lang:"fr"},
  {name:"Marc L.",role:"Gérant — restaurant, Charleroi",text:"Avant Aureus je payais 280€/mois pour 2 employés. Maintenant tout est automatique et je comprends enfin mes charges patronales.",stars:5,lang:"fr"},
  {name:"Nathalie P.",role:"Associée — cabinet RH, Mons",text:"Les 160 commissions paritaires sont toutes à jour. Pour mes clients CP 302, CP 226 et CP 200, Aureus applique les bons barèmes automatiquement.",stars:5,lang:"fr"},
  {name:"Julien F.",role:"CEO — scale-up tech, Bruxelles",text:"Onboarding de 12 employés en une semaine. Contrats, Dimona, fiches SEPA — tout généré automatiquement. Impressionnant.",stars:5,lang:"fr"},
  {name:"Amina K.",role:"Gérante — pharmacie, Anderlecht",text:"Mon premier employé avec Aureus : exonération patronale an 1 calculée automatiquement. 4.200€ économisés dès la première année.",stars:5,lang:"fr"},
  {name:"Pierre H.",role:"Expert-comptable indépendant, Tournai",text:"Je recommande Aureus à tous mes clients employeurs. L'export WinBooks est parfait, les DmfA XML passent du premier coup.",stars:5,lang:"fr"},
  {name:"Céline R.",role:"Fondatrice — ASBL, Ottignies",text:"Le bonus emploi 2026 est calculé automatiquement pour chacun de mes travailleurs. Avant je passais 3h par mois sur les formules SPF Finances.",stars:5,lang:"fr"},
  {name:"Pieter V.",role:"Zaakvoerder — IT-bedrijf, Gent",text:"Overstap van mijn vorige sociaal secretariaat naar Aureus in 2 dagen. Alle gegevens overgenomen, geen enkele onderbreking. De DmfA XML werkt perfect.",stars:5,lang:"nl"},
  {name:"Liesbet D.",role:"HR-manager — non-profit, Antwerpen",text:"132 modules, allemaal bereikbaar vanuit één dashboard. De automatische Dimona-herinnering 24u op voorhand heeft ons al meerdere boetes bespaard.",stars:5,lang:"nl"},
  {name:"Wout M.",role:"Oprichter — designbureau, Leuven",text:"Eerste werknemer aangenomen zonder enige kennis van Belgisch sociaal recht. Aureus heeft me stap voor stap begeleid: ONSS, contract, Dimona, loonbrief.",stars:5,lang:"nl"},
  {name:"An V.",role:"Accountant — 31 dossiers, Brugge",text:"Het multi-mandanten portaal is een gamechanger. Ik beheer 31 werkgevers vanuit één scherm. De Mahis-mandaten worden automatisch gegenereerd.",stars:5,lang:"nl"},
  {name:"Stijn B.",role:"Zaakvoerder — horeca, Mechelen",text:"Voor Aureus betaalde ik 320€/maand voor 3 werknemers. Nu is alles geautomatiseerd en begrijp ik eindelijk mijn patronale bijdragen.",stars:5,lang:"nl"},
  {name:"Karen J.",role:"Oprichtster — kinderopvang, Hasselt",text:"De lageloonvermindering en de vrijstelling eerste werknemer worden automatisch berekend. Ik bespaar meer dan 5.000€ per jaar.",stars:5,lang:"nl"},
  {name:"Raf D.",role:"Zelfstandige — consultant, Kortrijk",text:"SEPA pain.001 bestand in 3 klikken. Ik laad het rechtstreeks in mijn KBC-interface. Geen handmatige overschrijvingen meer.",stars:5,lang:"nl"},
  {name:"Sofie N.",role:"HR-directeur — vzw, Gent",text:"De WinBooks ACT export werkt perfect. Al mijn loonboekingen komen correct in de boekhouding terecht. Geen dubbele encodering meer.",stars:5,lang:"nl"},
  {name:"Tom V.",role:"CEO — e-commerce, Aalst",text:"Onboarding van 8 werknemers in één week. Contracten, Dimona, SEPA — alles automatisch gegenereerd. Indrukwekkend platform.",stars:5,lang:"nl"},
  {name:"Nele B.",role:"Zaakvoerster — kinesitherapie, Roeselare",text:"CP 330 correct geconfigureerd van bij de start. Alle barema's, sectorpremies en vakantiegeld automatisch berekend. Eindelijk rust.",stars:5,lang:"nl"},
  {name:"James R.",role:"CEO — fintech startup, Brussels",text:"Switched from my previous social secretariat to Aureus. All payroll data migrated in 48 hours, zero downtime. The DmfA XML passes ONSS validation first time, every time.",stars:5,lang:"en"},
  {name:"Emma T.",role:"HR Director — NGO, Brussels",text:"Managing 15 employees across 3 joint committees was a nightmare. Aureus handles it all automatically. The compliance dashboard gives me peace of mind.",stars:5,lang:"en"},
  {name:"David K.",role:"Founder — SaaS company, Ghent",text:"First Belgian employee hired with zero prior knowledge of Belgian social law. Aureus guided me through ONSS registration, contract, Dimona and first payslip.",stars:5,lang:"en"},
  {name:"Sophie L.",role:"CFO — scale-up, Antwerp",text:"The SEPA pain.001 export is flawless. One file, 23 employees, all salaries processed in seconds. Our bank accepts it every single month.",stars:5,lang:"en"},
  {name:"Michael B.",role:"Accountant — 18 clients, Brussels",text:"The multi-client portal saved me hours every month. All Mahis mandates generated automatically. Best tool I've used in 12 years of accounting.",stars:5,lang:"en"},
  {name:"Klaus M.",role:"Geschäftsführer — Beratung, Brüssel",text:"Wechsel von meinem früheren Sozialsekretariat zu Aureus in 3 Tagen. Alle Daten migriert, kein Ausfall. Das DmfA XML wird vom ONSS beim ersten Versuch akzeptiert.",stars:5,lang:"de"},
  {name:"Anna S.",role:"HR-Managerin — Nonprofit, Lüttich",text:"132 Module, alle von einem Dashboard aus zugänglich. Die automatische Dimona-Erinnerung 24h vorher hat uns bereits mehrere Bußgelder erspart.",stars:5,lang:"de"},
  {name:"Stefan H.",role:"Gründer — IT-Agentur, Eupen",text:"Erster belgischer Arbeitnehmer ohne Kenntnisse des belgischen Sozialrechts eingestellt. Aureus hat mich Schritt für Schritt begleitet.",stars:5,lang:"de"},
  {name:"Monika W.",role:"Steuerberaterin — 22 Mandanten, Lüttich",text:"Das Multi-Mandanten-Portal ist revolutionär. Ich verwalte 22 Arbeitgeber von einem Bildschirm. Mahis-Mandate automatisch generiert.",stars:5,lang:"de"},
  {name:"Frank B.",role:"Geschäftsführer — Handwerk, Arlon",text:"Der WinBooks-Export funktioniert einwandfrei. Alle Lohnbuchungen kommen korrekt in der Buchhaltung an. Keine doppelte Erfassung mehr.",stars:5,lang:"de"},
];

const TR = {
  fr: {
    headline: 'La gestion de paie\nbelge nouvelle génération.',
    sub: 'De la Dimona aux fiches de paie, Aureus Social Pro automatise l\'intégralité de vos obligations sociales belges.',
    connexion: 'Connexion', acces: 'Accédez à votre espace de gestion',
    email: 'EMAIL PROFESSIONNEL', pwd: 'MOT DE PASSE',
    btn: 'Se connecter →', btnMagic: 'Envoyer le lien →', loading: 'Connexion...',
    magic: '✨ Connexion par lien magique', backPwd: '← Connexion par mot de passe',
    secTitle: 'SÉCURITÉ',
    features: [
      { ico:'🏛', t:'Droit social belge 2026', d:'ONSS, DmfA, Belcotax, Dimona — mis à jour automatiquement.' },
      { ico:'⚡', t:'Automatisation complète', d:'Fiches de paie, virements SEPA, déclarations en quelques clics.' },
      { ico:'🔐', t:'Sécurité maximale', d:'Chiffrement AES-256-GCM · Hébergement UE · RGPD Art. 28.' },
      { ico:'🏢', t:'Multi-mandants', d:'Gérez plusieurs employeurs depuis un seul tableau de bord.' },
    ],
    guarantees: [
      { ico:'🏛', t:'Droit belge', d:'ONSS · DmfA · Dimona' },
      { ico:'💾', t:'Backup UE', d:'Frankfurt · données hébergées dans l\u2019UE' },
      { ico:'🔑', t:'2FA disponible', d:'Sécurité renforcée' },
      { ico:'📞', t:'Support dédié', d:'Réponse < 24h' },
    ],
    testimonials: [
      { name:'M. Janssen', role:'Expert-comptable · 23 dossiers', text:'Le portail multi-clients est exactement ce qu\'il manquait. Mandats Mahis générés automatiquement.' },
      { name:'S. Mbeki', role:'DRH · secteur associatif', text:'Migration depuis mon ancien secrétariat social en 3 jours. Toutes les données reprises, 0 interruption.' },
    ],
    stats: [
      { v:'132', l:'Modules actifs' }, { v:'160', l:'CP belges' }, { v:'8s', l:'Dimona' }, { v:'RGPD', l:'Conforme UE' },
    ],
    legal: 'Aureus IA SRL · BCE BE 1028.230.781 · Square Eugène Plasky 92-94 bte 11, 1030 Schaerbeek\nHébergement UE · Chiffrement AES-256-GCM · Certifié RGPD Art. 28',
    legalShort: 'Aureus IA SRL · BCE BE 1028.230.781\nDonnées hébergées en UE · Chiffrement AES-256',
    noAccount: 'Pas encore de compte ?',
    askAccess: 'Demander un accès →',
    noAccountHint: 'Les accès sont ouverts par notre équipe, il n’y a pas d’inscription automatique.',
  },
  nl: {
    headline: 'Het Belgische loonbeheer\nvan de toekomst.',
    sub: 'Van Dimona tot loonfiches, Aureus Social Pro automatiseert al uw Belgische sociale verplichtingen.',
    connexion: 'Inloggen', acces: 'Toegang tot uw beheersomgeving',
    email: 'PROFESSIONEEL E-MAIL', pwd: 'WACHTWOORD',
    btn: 'Inloggen →', btnMagic: 'Link versturen →', loading: 'Bezig...',
    magic: '✨ Inloggen via magische link', backPwd: '← Inloggen met wachtwoord',
    secTitle: 'BEVEILIGING',
    features: [
      { ico:'🏛', t:'Belgisch sociaal recht 2026', d:'ONSS, DmfA, Belcotax, Dimona — automatisch bijgewerkt.' },
      { ico:'⚡', t:'Volledige automatisering', d:'Loonfiches, SEPA-overschrijvingen, aangiften in enkele klikken.' },
      { ico:'🔐', t:'Maximale beveiliging', d:'AES-256-GCM versleuteling · EU-hosting · AVG Art. 28.' },
      { ico:'🏢', t:'Multi-mandanten', d:'Beheer meerdere werkgevers vanuit één dashboard.' },
    ],
    guarantees: [
      { ico:'🏛', t:'Belgisch recht', d:'ONSS · DmfA · Dimona' },
      { ico:'💾', t:'EU-backup', d:'Frankfurt · gegevens gehost in de EU' },
      { ico:'🔑', t:'2FA beschikbaar', d:'Versterkte beveiliging' },
      { ico:'📞', t:'Toegewijde support', d:'Antwoord < 24u' },
    ],
    testimonials: [
      { name:'M. Janssen', role:'Accountant · 23 dossiers', text:'Het multi-klanten portaal is precies wat ontbrak. Mahis-mandaten automatisch gegenereerd.' },
      { name:'S. Mbeki', role:'HR-directeur · vzw-sector', text:'Migratie van mijn vorige sociaal secretariaat in 3 dagen. Alle gegevens overgenomen, 0 onderbreking.' },
    ],
    stats: [
      { v:'132', l:'Actieve modules' }, { v:'160', l:'Belgische PC' }, { v:'8s', l:'Dimona' }, { v:'AVG', l:'Conform EU' },
    ],
    legal: 'Aureus IA SRL · BCE BE 1028.230.781 · Square Eugène Plasky 92-94 bte 11, bus 10, 1030 Schaarbeek\nEU-hosting · AES-256-GCM versleuteling · AVG Art. 28 gecertificeerd',
    legalShort: 'Aureus IA SRL · BCE BE 1028.230.781\nGegevens gehost in EU · AES-256 versleuteling',
    noAccount: 'Nog geen account?',
    askAccess: 'Toegang aanvragen →',
    noAccountHint: 'Toegangen worden door ons team geopend, er is geen automatische registratie.',
  },
  // EN et DE retirés le 05/09/2026 (décision fondateur) : le sélecteur n'offre plus
  // que FR et NL ; un ancien réglage « en »/« de » retombe sur FR (TR[l] absent).
};

export default function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('password');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const [dark, setDark] = useState(true);
  const [lang, setLang] = useState('fr');
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [slide, setSlide] = useState(0);
  // 2FA enrolement obligatoire pour admin
  const [mfaEnrollRequired, setMfaEnrollRequired] = useState(false);
  const [mfaEnrollQR, setMfaEnrollQR] = useState('');
  const [mfaEnrollSecret, setMfaEnrollSecret] = useState('');

  // Détection déconnexion par inactivité (vient de /login?reason=idle)
  const [idleNotice, setIdleNotice] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason') || (() => { try { return sessionStorage.getItem('aureus_logout_reason'); } catch { return null; } })();
    if (reason === 'idle') {
      setIdleNotice(true);
      try { sessionStorage.removeItem('aureus_logout_reason'); } catch {}
    }
  }, []);
  const [mfaEnrollFactorId, setMfaEnrollFactorId] = useState('');
  const [mfaEnrollCode, setMfaEnrollCode] = useState('');
  const [mfaEnrollError, setMfaEnrollError] = useState('');
  const { getToken: getRecaptchaToken } = useRecaptcha();

  useEffect(() => {
    try {
      const s = localStorage.getItem('aureus_theme'); if (s) setDark(s !== 'light');
      const l = localStorage.getItem('aureus_lang'); if (l && TR[l]) setLang(l);
    } catch(e) {}
    // Auto-login via demo token (URL only, or localStorage if never had real login)
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('demo_token');
      const hadRealLogin = (() => { try { return localStorage.getItem('aureus_had_real_login') === 'true'; } catch(e) { return false; } })();
      const savedToken = !hadRealLogin ? (() => { try { return localStorage.getItem('aureus_demo_token'); } catch(e) { return null; } })() : null;
      const demoToken = urlToken || savedToken;
      if (demoToken) {
        // Generate or retrieve device ID
        let deviceId;
        try { deviceId = localStorage.getItem('aureus_device_id'); } catch(e) {}
        if (!deviceId) { deviceId = Math.random().toString(36).slice(2) + Date.now().toString(36); try { localStorage.setItem('aureus_device_id', deviceId); } catch(e) {} }
        fetch('/api/demo-token?token=' + encodeURIComponent(demoToken) + '&device_id=' + encodeURIComponent(deviceId))
          .then(r => r.json())
          .then(d => {
            if (d.valid) {
              const demoRole = d.role || 'readonly';
              try { localStorage.setItem('aureus_demo_token', demoToken); } catch(e) {}
              // Seed demo data then auto-login with the correct role
              // (token requis côté serveur — sinon /api/demo rejette la requête)
              fetch('/api/demo?token=' + encodeURIComponent(demoToken), { method: 'POST' })
                .then(() => {
                  window.history.replaceState({}, '', window.location.pathname + '?demo=true');
                  onLogin({ email: 'demo-prospect@aureus-ia.com', role: demoRole, app_metadata: { role: demoRole }, user_metadata: { role: demoRole, demo: true } });
                })
                .catch(() => onLogin({ email: 'demo-prospect@aureus-ia.com', role: demoRole, app_metadata: { role: demoRole }, user_metadata: { role: demoRole, demo: true } }));
            } else {
              // Token expiré — nettoyer
              try { localStorage.removeItem('aureus_demo_token'); } catch(e) {}
            }
          })
          .catch(() => {});
      }
    }
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % 2), 5000);
    return () => clearInterval(t);
  }, []);

  const toggleTheme = () => {
    const next = !dark; setDark(next);
    try { localStorage.setItem('aureus_theme', next ? 'dark' : 'light'); } catch(e) {}
  };

  const switchLang = (l) => {
    setLang(l);
    try { localStorage.setItem('aureus_lang', l); } catch(e) {}
  };

  const handleMfaVerify = async () => {
    if (!mfaCode || mfaCode.length !== 6) { setError('Code MFA requis (6 chiffres)'); return; }
    setLoading(true); setError('');
    try {
      await challengeMFA(supabase, mfaCode);
      try { localStorage.removeItem('aureus_demo_token'); localStorage.setItem('aureus_had_real_login', 'true'); } catch(e) {}
      onLogin(pendingUser);
    } catch (err) { setError(err.message || 'Code MFA invalide'); }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    if (!supabase) { onLogin({ email: email || 'demo@aureus-ia.com', role: 'admin' }); setLoading(false); return; }
    try {
      // reCAPTCHA v3 verification
      const recaptchaToken = await getRecaptchaToken('login');
      if (recaptchaToken) {
        const rcRes = await fetch('/api/recaptcha/verify', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: recaptchaToken, action: 'login', email })
        }).then(r => r.json()).catch(() => ({ success: true }));
        if (!rcRes.success) { setError('Vérification de sécurité échouée. Réessayez.'); setLoading(false); return; }
      }

      if (mode === 'magic') {
        // SÉCURITÉ : shouldCreateUser:false — le lien magique NE crée JAMAIS de compte.
        // Seul l'admin crée des comptes via /api/invite-role|invite-client (service role).
        // Sans ça, n'importe qui s'auto-inscrit en tapant son email (incident web-soul 08/07/2026).
        const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false, emailRedirectTo: TENANT.appUrl } });
        if (error) throw error;
        setError('✅ ' + t.btn + ' ' + email);
        fetch('/api/auth/log-login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, success: true, reason: 'otp_sent' }) }).catch(() => {});
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          // Log echec
          const lockRes = await fetch('/api/auth/log-login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, success: false, reason: error.message }) }).then(r => r.json()).catch(() => ({}));
          if (lockRes?.locked) { setError(lockRes.message); setLoading(false); return; }
          throw error;
        }
        if (data?.user) {
          // Log succes
          // Le jeton de session PROUVE la connexion : la route ne croit plus l'email/userId
          // du corps, elle prend l'identité du jeton (audit sécurité du 06/10/2026).
          const _jeton = data.session?.access_token;
          fetch('/api/auth/log-login', { method: 'POST', headers: { 'Content-Type': 'application/json', ...(_jeton ? { Authorization: 'Bearer ' + _jeton } : {}) }, body: JSON.stringify({ email, userId: data.user.id, success: true }) }).catch(() => {});
          
          // Récupérer le rôle de l'utilisateur
          const userRole = (data.user.app_metadata?.role || '').toLowerCase();
          
          // Vérifier si MFA est configuré pour cet utilisateur
          const factors = await listMFAFactors(supabase);
          
          if (factors && factors.length > 0) {
            // MFA déjà configuré → demander le code
            setPendingUser(data.user);
            setMfaRequired(true);
          } else {
            // Pas de 2FA configuré → connexion directe (pour tous les rôles)
            // Le 2FA peut être activé volontairement depuis les paramètres du compte
            try { localStorage.removeItem('aureus_demo_token'); localStorage.setItem('aureus_had_real_login', 'true'); } catch(e) {}
            onLogin(data.user);
          }
        }
      }
    } catch (err) { setError(err.message || 'Erreur de connexion'); }
    setLoading(false);
  };

  // Valider l'enrôlement 2FA et activer
  const handleMfaEnrollVerify = async () => {
    if (!mfaEnrollCode || mfaEnrollCode.length !== 6) { setMfaEnrollError('Code 6 chiffres requis'); return; }
    setLoading(true); setMfaEnrollError('');
    try {
      await verifyAndActivateMFA(supabase, mfaEnrollFactorId, mfaEnrollCode);
      // 2FA activé → connexion complète
      try { localStorage.removeItem('aureus_demo_token'); localStorage.setItem('aureus_had_real_login', 'true'); } catch(e) {}
      onLogin(pendingUser);
    } catch (err) {
      setMfaEnrollError(err.message || 'Code invalide — vérifiez votre app TOTP');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) return setError("Entrez votre email d'abord");
    setLoading(true); setError('');
    try {
      // reCAPTCHA v3 verification
      const recaptchaToken = await getRecaptchaToken('forgot_password');
      if (recaptchaToken) {
        const rcRes = await fetch('/api/recaptcha/verify', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: recaptchaToken, action: 'forgot_password', email })
        }).then(r => r.json()).catch(() => ({ success: true }));
        if (!rcRes.success) { setError('Vérification de sécurité échouée. Réessayez.'); setLoading(false); return; }
      }

      // ⚠️ DIRECTEMENT sur /reset-password — surtout PAS par /api/auth/callback
      // (corrigé le 01/09/2026). Ce détour est une route SERVEUR : elle ne lit
      // que la query string. Or Supabase livre le jeton de récupération dans le
      // FRAGMENT de l'URL (après le #), et un fragment n'est JAMAIS envoyé au
      // serveur — le navigateur le coupe avant la requête. Le détour ne pouvait
      // donc rien apporter, et pouvait tout perdre : le client arrivait sur une
      // page vide qui lui annonçait au bout de 8 secondes que le lien ne marchait
      // pas. Un client a boucle là-dessus pendant une semaine.
      // La page /reset-password, elle, lit le fragment ET le paramètre `code`
      // (detectSessionInUrl est actif) : les deux flux Supabase y arrivent entiers.
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: appUrl('/reset-password')
      });
      if (error) throw error;
      setResetSent(true);
      setError('✅ Email envoyé ! Vérifiez votre boîte mail.');
    } catch(err) {
      setError(err.message || "Erreur lors de l'envoi");
    }
    setLoading(false);
  };

  const t = TR[lang] || TR.fr;

  // Couleurs
  const bg      = dark ? '#0c0b09' : '#f5f3ef';
  const bgL     = dark ? 'linear-gradient(135deg,#0c0b09 0%,#181613 60%,#1f1c17 100%)' : 'linear-gradient(135deg,#f5f3ef 0%,#ede9e1 100%)';
  const bgR     = dark ? '#111009' : '#ffffff';
  const ink     = dark ? '#e8e6e0' : '#0e0d0a';
  const mist    = dark ? '#7a7770' : '#1a1916';
  const stone   = dark ? '#5e5c56' : '#2a2826';
  const gold    = '#c6a34e';
  const border  = dark ? 'rgba(198,163,78,.15)' : 'rgba(14,13,10,.15)';
  const inputBg = dark ? 'rgba(0,0,0,.35)' : '#ffffff';
  const cardBg  = dark ? 'rgba(255,255,255,.03)' : 'rgba(0,0,0,.04)';

  // ── Écran ENRÔLEMENT 2FA OBLIGATOIRE pour admin ──
  if (mfaEnrollRequired) {
    return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#0c0b09',fontFamily:"'Inter',system-ui,sans-serif"}}>
        <div style={{width:400,padding:32,background:'rgba(255,255,255,.03)',border:'2px solid rgba(198,163,78,.4)',borderRadius:16}}>
          <div style={{textAlign:'center',marginBottom:20}}>
            <div style={{fontSize:28,marginBottom:8}}>🔐</div>
            <div style={{fontSize:18,fontWeight:800,color:'#c6a34e'}}>Activation 2FA obligatoire</div>
            <div style={{fontSize:11,color:'#888',marginTop:6,lineHeight:1.5}}>
              Le compte <strong style={{color:'#d4d0c8'}}>admin</strong> requiert une authentification à deux facteurs.<br/>
              Scannez le QR code avec <strong style={{color:'#c6a34e'}}>Google Authenticator</strong> ou <strong style={{color:'#c6a34e'}}>Authy</strong>.
            </div>
          </div>

          {/* QR Code */}
          {mfaEnrollQR && (
            <div style={{textAlign:'center',margin:'16px 0'}}>
              <img src={mfaEnrollQR} alt="QR Code 2FA" style={{width:180,height:180,borderRadius:8,background:'white',padding:8}} />
            </div>
          )}

          {/* Clé secrète manuelle */}
          {mfaEnrollSecret && (
            <div style={{background:'rgba(198,163,78,.08)',border:'1px solid rgba(198,163,78,.2)',borderRadius:8,padding:'10px 14px',marginBottom:16,textAlign:'center'}}>
              <div style={{fontSize:10,color:'#888',marginBottom:4}}>Clé secrète (saisie manuelle)</div>
              <div style={{fontFamily:'monospace',fontSize:13,color:'#c6a34e',letterSpacing:2,wordBreak:'break-all'}}>{mfaEnrollSecret}</div>
            </div>
          )}

          {/* Saisie code */}
          <div style={{marginBottom:12}}>
            <div style={{fontSize:11,color:'#888',marginBottom:6}}>Entrez le code 6 chiffres affiché dans votre app :</div>
            <input
              type="text" inputMode="numeric" maxLength={6}
              value={mfaEnrollCode}
              onChange={e => setMfaEnrollCode(e.target.value.replace(/\D/g,''))}
              onKeyDown={e => e.key==='Enter' && handleMfaEnrollVerify()}
              placeholder="000000"
              style={{
                width:'100%',boxSizing:'border-box',padding:'12px 14px',
                background:'rgba(255,255,255,.05)',border:`1px solid ${mfaEnrollError?'#ef4444':'rgba(198,163,78,.3)'}`,
                borderRadius:8,color:'#e8e6e0',fontSize:20,fontFamily:'monospace',
                letterSpacing:8,textAlign:'center',outline:'none',
              }}
              autoFocus
            />
          </div>

          {mfaEnrollError && <div style={{color:'#ef4444',fontSize:12,marginBottom:10,textAlign:'center'}}>{mfaEnrollError}</div>}

          <button
            onClick={handleMfaEnrollVerify}
            disabled={loading || mfaEnrollCode.length !== 6}
            style={{width:'100%',padding:'12px',borderRadius:8,border:'none',
              background: mfaEnrollCode.length===6 ? '#c6a34e' : 'rgba(198,163,78,.3)',
              color: mfaEnrollCode.length===6 ? '#0c0b09' : '#888',
              fontWeight:700,fontSize:14,cursor:mfaEnrollCode.length===6?'pointer':'not-allowed'}}
          >
            {loading ? '⏳ Activation...' : '✅ Activer le 2FA et accéder'}
          </button>

          <div style={{marginTop:16,padding:12,background:'rgba(239,68,68,.06)',border:'1px solid rgba(239,68,68,.2)',borderRadius:8,fontSize:11,color:'#fca5a5',lineHeight:1.5}}>
            ⚠️ <strong>Important :</strong> Conservez votre clé secrète en lieu sûr. Sans elle, vous ne pourrez plus accéder à votre compte en cas de perte du téléphone.
          </div>
        </div>
      </div>
    );
  }

  // ── Écran MFA si 2FA configuré ──
  if (mfaRequired) {
    return (
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#0c0b09',fontFamily:"'Inter',system-ui,sans-serif"}}>
        <div style={{width:360,padding:32,background:'rgba(255,255,255,.03)',border:'1px solid rgba(198,163,78,.15)',borderRadius:16}}>
          <div style={{textAlign:'center',marginBottom:24}}>
            <div style={{fontSize:36,marginBottom:8}}>🔐</div>
            <div style={{fontSize:18,fontWeight:700,color:'#e8e6e0'}}>Vérification 2FA</div>
            <div style={{fontSize:11,color:'#5e5c56',marginTop:4}}>Code TOTP depuis votre application (Google Authenticator, Authy)</div>
          </div>
          <input
            type="text" maxLength={6} value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/\D/g,''))}
            placeholder="000000"
            onKeyDown={e => e.key === 'Enter' && handleMfaVerify()}
            style={{width:'100%',padding:'12px 16px',background:'rgba(0,0,0,.35)',border:'1px solid rgba(198,163,78,.2)',borderRadius:8,color:'#e8e6e0',fontSize:22,fontFamily:'monospace',letterSpacing:8,textAlign:'center',boxSizing:'border-box',marginBottom:12}}
          />
          {error && <div style={{color:'#ef4444',fontSize:11,marginBottom:8,textAlign:'center'}}>{error}</div>}
          <button onClick={handleMfaVerify} disabled={loading || mfaCode.length !== 6}
            style={{width:'100%',padding:'12px',borderRadius:8,border:'none',background:'rgba(198,163,78,.15)',color:'#c6a34e',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
            {loading ? '⏳ Vérification…' : '✓ Valider'}
          </button>
          <button onClick={() => { setMfaRequired(false); setPendingUser(null); setMfaCode(''); }}
            style={{width:'100%',padding:'8px',borderRadius:8,border:'none',background:'transparent',color:'#5e5c56',fontSize:11,cursor:'pointer',fontFamily:'inherit',marginTop:8}}>
            ← Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-shell" style={{ display:'flex', minHeight:'100vh', background:bg, fontFamily:"'Inter',system-ui,sans-serif", position:'relative' }}>
      {/* Mobile (≤ 900 px) : le formulaire d'abord, pleine largeur, le panneau
          institutionnel en dessous. Sans ceci, le flex à deux colonnes écrasait
          la colonne gauche en une bande de 200 px et poussait le formulaire hors
          de l'écran (capture fondateur 05/09/2026). CSS pur : aucun état React,
          donc pas de flash desktop→mobile au premier rendu. */}
      <style>{`
        @media (max-width: 900px) {
          .login-shell { flex-direction: column; }
          .login-left { order: 2; padding: 32px 20px 40px !important; }
          /* box-sizing : width 100 % + 20 px de marge de chaque côté faisait 430 px sur un écran de 390 (la boîte était en
             content-box). Mesuré dans Chrome mobile le 06/10/2026 : window.innerWidth 430 pour un visualViewport de 390, le
             champ e-mail de x = 20 à x = 410, la page défilait de côté. Défaut apparu avec la correction du 05/09, que le
             test de rendu en jsdom (aucun moteur de mise en page) ne pouvait pas voir. */
          .login-right { order: 1; width: 100% !important; box-sizing: border-box; border-left: none !important; padding: 72px 20px 32px !important; }
          .login-right > div { width: 100% !important; max-width: 400px; }
          .login-hero { font-size: 26px !important; }
        }
      `}</style>

      {/* Controls top-right */}
      <div style={{ position:'fixed', top:16, right:16, zIndex:999, display:'flex', gap:8, alignItems:'center' }}>
        {/* Lang switcher */}
        <div style={{ display:'flex', gap:4, background:cardBg, border:`1px solid ${border}`, borderRadius:8, padding:'4px 6px' }}>
          {['fr','nl'].map(l => (
            <button key={l} onClick={() => switchLang(l)}
              style={{ background: lang===l ? gold : 'transparent', color: lang===l ? '#0c0b09' : mist, border:'none', borderRadius:5, padding:'3px 8px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
        {/* Theme toggle */}
        <button onClick={toggleTheme}
          style={{ background:cardBg, border:`1px solid ${border}`, borderRadius:8, width:36, height:36, cursor:'pointer', fontSize:16, display:'flex', alignItems:'center', justifyContent:'center', transition:'all .2s' }}>
          {dark ? '☀️' : '🌙'}
        </button>
      </div>

      {/* LEFT — Institutionnel */}
      <div className="login-left" style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'60px 72px', background:bgL, minWidth:0, overflowY:'auto' }}>
        <div>
          {/* Logo */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:48 }}>
            <AureusLogo size={44} priority />
            <div>
              <div style={{ fontSize:16, fontWeight:800, color:gold, letterSpacing:'3px' }}>AUREUS</div>
              <div style={{ fontSize:10, color:mist, letterSpacing:'3px' }}>SOCIAL PRO</div>
            </div>
          </div>

          {/* Headline */}
          <h1 className="login-hero" style={{ fontSize:36, fontWeight:800, color:ink, lineHeight:1.25, marginBottom:16, maxWidth:480, whiteSpace:'pre-line' }}>
            {t.headline.split('\n').map((l,i) => i===1 ? <span key={i} style={{color:gold}}>{l}</span> : <span key={i}>{l}<br/></span>)}
          </h1>
          <p style={{ fontSize:15, color:mist, lineHeight:1.75, maxWidth:420, marginBottom:36 }}>{t.sub}</p>

          {/* Stats */}
          <div style={{ display:'flex', gap:14, flexWrap:'wrap', marginBottom:40 }}>
            {t.stats.map(k => (
              <div key={k.l} style={{ background:cardBg, border:`1px solid ${border}`, borderRadius:10, padding:'12px 18px', textAlign:'center' }}>
                <div style={{ fontSize:20, fontWeight:800, color:gold }}>{k.v}</div>
                <div style={{ fontSize:10, color:stone, marginTop:2, letterSpacing:.5 }}>{k.l}</div>
              </div>
            ))}
          </div>

          {/* Features */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, maxWidth:480, marginBottom:40 }}>
            {t.features.map(f => (
              <div key={f.t} style={{ background:cardBg, border:`1px solid ${border}`, borderRadius:10, padding:'14px 16px' }}>
                <div style={{ fontSize:18, marginBottom:6 }}>{f.ico}</div>
                <div style={{ fontSize:12, fontWeight:700, color:ink, marginBottom:3 }}>{f.t}</div>
                <div style={{ fontSize:11, color:mist, lineHeight:1.6 }}>{f.d}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Témoignages */}
        <div style={{ borderTop:`1px solid ${border}`, paddingTop:20, marginTop:8 }}>
          {(() => {
            const filtered = TESTIMONIALS_ALL.filter(a => a.lang === lang);
            const cur = filtered[slide % filtered.length];
            return cur ? (
              <>
                <div style={{ fontSize:16, color:gold, marginBottom:8 }}>★★★★★</div>
                <p style={{ fontSize:13, color:mist, fontStyle:'italic', lineHeight:1.7, marginBottom:10, minHeight:52 }}>
                  "{cur.text}"
                </p>
                <div style={{ fontSize:13, fontWeight:700, color:ink }}>{cur.name}</div>
                <div style={{ fontSize:11, color:stone, marginBottom:12 }}>{cur.role}</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button onClick={()=>setSlide(s=>(s-1+filtered.length)%filtered.length)}
                    style={{background:'none',border:`1px solid ${border}`,borderRadius:'50%',width:28,height:28,cursor:'pointer',color:mist,fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>←</button>
                  <div style={{ display:'flex', gap:4 }}>
                    {filtered.map((_,i)=>(
                      <div key={i} onClick={()=>setSlide(i)}
                        style={{width:i===slide%filtered.length?16:5,height:5,borderRadius:3,background:i===slide%filtered.length?gold:border,cursor:'pointer',transition:'all .3s'}}/>
                    ))}
                  </div>
                  <button onClick={()=>setSlide(s=>(s+1)%filtered.length)}
                    style={{background:'none',border:`1px solid ${border}`,borderRadius:'50%',width:28,height:28,cursor:'pointer',color:mist,fontSize:14,display:'flex',alignItems:'center',justifyContent:'center'}}>→</button>
                  <span style={{fontSize:10,color:stone,marginLeft:4}}>{slide%filtered.length+1}/{filtered.length}</span>
                </div>
              </>
            ) : null;
          })()}
        </div>

                <div style={{ marginTop:20, fontSize:10, color:stone, lineHeight:1.8, whiteSpace:'pre-line' }}>{t.legal}</div>
      </div>

      {/* RIGHT — Formulaire */}
      <div className="login-right" style={{ width:460, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', background:bgR, borderLeft:`1px solid ${border}`, padding:'80px 0 40px' }}>
        <div style={{ width:340 }}>
          <div style={{ marginBottom:28 }}>
            <div style={{ fontSize:24, fontWeight:800, color:ink, marginBottom:5 }}>{t.connexion}</div>
            <div style={{ fontSize:13, color:mist }}>{t.acces}</div>
          </div>

          {/* Badges */}
          <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
            {['🔒 SSL','🇧🇪 EU','🛡 RGPD'].map(b => (
              <span key={b} style={{ fontSize:10, background:cardBg, border:`1px solid ${border}`, borderRadius:20, padding:'3px 10px', color:stone, fontWeight:600 }}>{b}</span>
            ))}
          </div>

          {idleNotice && (
            <div style={{
              padding: '12px 14px',
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 12,
              background: 'rgba(251,146,60,.10)',
              color: '#fb923c',
              border: '1px solid rgba(251,146,60,.3)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              lineHeight: 1.5,
            }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>⏰</span>
              <div>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>
                  Session expirée par inactivité
                </div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>
                  Pour des raisons de sécurité, vous avez été déconnecté(e) après 2 h d'inactivité. Reconnectez-vous pour reprendre.
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:12,
              background: error.startsWith('✅') ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)',
              color: error.startsWith('✅') ? '#22c55e' : '#ef4444',
              border:`1px solid ${error.startsWith('✅') ? 'rgba(34,197,94,.2)' : 'rgba(239,68,68,.2)'}` }}>
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:10, fontWeight:700, color:stone, marginBottom:5, letterSpacing:.8 }}>{t.email}</label>
              {/* name / id / autoComplete OBLIGATOIRES sur un formulaire de
                  connexion. Sans eux, macOS (Safari et Chrome avec le trousseau)
                  ne reconnaît pas le couple identifiant + mot de passe : la
                  surcouche « mot de passe fort » se superpose au champ et avale
                  les frappes. Symptôme rapporté par IT Place : « impossible
                  d'écrire mon mot de passe et donc de me connecter » sur Mac.
                  MonCompte.js appliquait déjà ces attributs, l'écran de
                  connexion était le seul resté sans. */}
              <input type="email" name="username" id="login-email" autoComplete="username"
                autoCapitalize="none" autoCorrect="off" spellCheck={false}
                value={email} onChange={e => setEmail(e.target.value)} required placeholder="nom@entreprise.be"
                style={{ width:'100%', padding:'13px 16px', borderRadius:10, border:`1px solid ${border}`, background:inputBg, color:ink, fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box', transition:'border .2s' }}
                onFocus={e => e.target.style.borderColor=gold} onBlur={e => e.target.style.borderColor=border} />
            </div>

            {mode === 'password' && (
              <div style={{ marginBottom:22 }}>
                <label style={{ display:'block', fontSize:10, fontWeight:700, color:stone, marginBottom:5, letterSpacing:.8 }}>{t.pwd}</label>
                <input type="password" name="password" id="login-password" autoComplete="current-password"
                  autoCapitalize="none" autoCorrect="off" spellCheck={false} required
                  value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••"
                  style={{ width:'100%', padding:'13px 16px', borderRadius:10, border:`1px solid ${border}`, background:inputBg, color:ink, fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box', transition:'border .2s' }}
                  onFocus={e => e.target.style.borderColor=gold} onBlur={e => e.target.style.borderColor=border} />
                <div style={{ textAlign:'right', marginTop:6 }}>
                  <button type="button" onClick={() => setResetMode(true)}
                    style={{ background:'none', border:'none', color:gold, fontSize:11, cursor:'pointer', fontFamily:'inherit', opacity:.8 }}>
                    Mot de passe oublié ?
                  </button>
                </div>
              </div>
            )}

            <button type="submit" disabled={loading}
              onMouseOver={e=>{ if(!loading){ e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 8px 26px rgba(184,145,58,.42)'; } }}
              onMouseOut={e=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow = loading ? 'none' : '0 6px 20px rgba(184,145,58,.30)'; }}
              style={{ width:'100%', padding:'15px', borderRadius:11, border:'none', background: loading ? (dark?'#3a3830':'#ccc') : 'linear-gradient(135deg,#E2BC63 0%,#C6A34E 50%,#A6863A 100%)', color:'#1a1505', fontSize:14.5, fontWeight:700, cursor: loading ? 'wait' : 'pointer', fontFamily:'inherit', letterSpacing:'.3px', boxShadow: loading ? 'none' : '0 6px 20px rgba(184,145,58,.30)', transition:'transform .18s, box-shadow .18s' }}>
              {loading ? t.loading : mode === 'magic' ? t.btnMagic : t.btn}
            </button>
          </form>

          {resetMode && (
            <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,.7)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
              <div style={{ background:dark?'#111009':'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:400, border:`1px solid ${border}` }}>
                <div style={{ fontSize:18, fontWeight:800, color:gold, marginBottom:8 }}>🔑 Réinitialiser le mot de passe</div>
                <div style={{ fontSize:12, color:stone, marginBottom:20 }}>Un lien de réinitialisation sera envoyé à votre adresse email.</div>
                {!resetSent ? (
                  <>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      style={{ width:'100%', padding:'12px 16px', borderRadius:10, border:`1px solid ${border}`, background:inputBg, color:ink, fontSize:13, fontFamily:'inherit', outline:'none', boxSizing:'border-box', marginBottom:16 }} />
                    <button onClick={handleForgotPassword} disabled={loading}
                      style={{ width:'100%', padding:'13px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#E2BC63 0%,#C6A34E 50%,#A6863A 100%)', color:'#1a1505', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit', marginBottom:12, boxShadow:'0 5px 16px rgba(184,145,58,.28)' }}>
                      {loading ? 'Envoi...' : 'Envoyer le lien'}
                    </button>
                  </>
                ) : (
                  <div style={{ padding:'16px', background:'rgba(34,197,94,.08)', border:'1px solid rgba(34,197,94,.2)', borderRadius:10, color:'#22c55e', fontSize:13, textAlign:'center', marginBottom:16 }}>
                    ✅ Email envoyé ! Vérifiez votre boîte mail et vos spams.
                  </div>
                )}
                <button onClick={() => { setResetMode(false); setResetSent(false); setError(''); }}
                  style={{ width:'100%', padding:'10px', borderRadius:10, border:`1px solid ${border}`, background:'transparent', color:stone, fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
                  ← Retour à la connexion
                </button>
              </div>
            </div>
          )}

          <div style={{ textAlign:'center', marginTop:14 }}>
            <button type="button" onClick={() => setMode(mode === 'password' ? 'magic' : 'password')}
              style={{ background:'none', border:'none', color:gold, fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>
              {mode === 'password' ? t.magic : t.backPwd}
            </button>
          </div>

          {/* Sans ce bloc, un visiteur SANS compte n'a aucune porte : ni bouton
              d'inscription (volontaire — les accès sont créés par l'équipe), ni
              explication. Signalé par une prospecte le 01/08/2026 (« j'ai
              téléchargé l'application mais je n'ai pas de bouton d'inscription »)
              — d'autant que /early-access a été supprimée le 02/08 (#1449).
              /contact est la porte publique maintenue (/demo y redirige). */}
          <div style={{ textAlign:'center', marginTop:16, fontSize:12, color:stone, lineHeight:1.6 }}>
            <span>{t.noAccount} </span>
            <a href="/contact" style={{ color:gold, fontWeight:700, textDecoration:'none' }}>{t.askAccess}</a>
            <div style={{ fontSize:10.5, opacity:.8, marginTop:3 }}>{t.noAccountHint}</div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'24px 0' }}>
            <div style={{ flex:1, height:1, background:border }}/>
            <span style={{ fontSize:10, color:stone, fontWeight:700, letterSpacing:1 }}>{t.secTitle}</span>
            <div style={{ flex:1, height:1, background:border }}/>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {t.guarantees.map(g => (
              <div key={g.t} style={{ background:cardBg, border:`1px solid ${border}`, borderRadius:8, padding:'11px 13px' }}>
                <div style={{ fontSize:15, marginBottom:3 }}>{g.ico}</div>
                <div style={{ fontSize:11, fontWeight:700, color:ink }}>{g.t}</div>
                <div style={{ fontSize:10, color:stone }}>{g.d}</div>
              </div>
            ))}
          </div>

          <div style={{ textAlign:'center', marginTop:28, fontSize:10, color:stone, lineHeight:1.8, whiteSpace:'pre-line' }}>{t.legalShort}</div>
        </div>
      </div>
    </div>
  );
}
